import { zodResolver } from "@hookform/resolvers/zod";
import { useInvestorEntityUpdate } from "gen/endpoints/investor-entity/investor-entity";
import { InvestorEntityData } from "gen/model/investorEntityData";
import { InvestorEntityDataRequest } from "gen/model/investorEntityDataRequest";
import { investorEntityUpdateBody } from "gen/schemas/investor-entity/investor-entity";
import { useCallback } from "react";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormBooleanToggleButtonGroup } from "shared/components/button/ToggleButton";
import { FormProps } from "shared/components/form/Form";
import { FormJurisdictionSelect } from "shared/components/input/select/JurisdictionSelect";
import { FormTextInput } from "shared/components/input/text/TextInput";

export interface InvestorEntityEditFormProps {
  investorEntity: InvestorEntityData;
}

export const useInvestorEntityEditForm = ({
  investorEntity,
}: InvestorEntityEditFormProps): FormProps<
  InvestorEntityData,
  InvestorEntityData
> => {
  const form = useForm<InvestorEntityDataRequest>({
    resolver: zodResolver(investorEntityUpdateBody),
    defaultValues: investorEntity as DefaultValues<InvestorEntityData>,
  });

  const { mutateAsync: updateInvestorEntity } = useInvestorEntityUpdate();

  const onSubmit = useCallback(
    (data: InvestorEntityDataRequest) =>
      updateInvestorEntity({
        id: investorEntity.id!,
        data,
      }),
    [investorEntity.id, updateInvestorEntity],
  );

  return { form, onSubmit } as const;
};

export const InvestorEntityFormFields = () => {
  const { t } = useTranslation(["investorEntity", "entity"]);
  return (
    <>
      <FormTextInput name="legal_name" title={t("entity:legalName")} />
      <FormTextInput name="external_id" title={t("entity:externalId")} />
      <FormJurisdictionSelect />
      <FormBooleanToggleButtonGroup
        name="metadata.is_foreign"
        title={t("investorEntity:isForeign")}
      />
      <FormBooleanToggleButtonGroup
        name="metadata.is_tax_exempt"
        title={t("investorEntity:isTaxExempt")}
      />
      <FormBooleanToggleButtonGroup
        name="metadata.erisa_investor"
        title={t("investorEntity:erisaInvestor")}
      />
      <FormBooleanToggleButtonGroup
        name="metadata.affiliated_with_sponsor"
        title={t("investorEntity:affiliatedWithSponsor")}
      />
      <FormBooleanToggleButtonGroup
        name="metadata.is_bank_holding_company"
        title={t("investorEntity:isBankHoldingCompany")}
      />
    </>
  );
};
